import { cn } from '@goaltree/ui/lib/utils'
import { CheckIcon, MapPinIcon, MapPinOffIcon, PencilIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { geocodeCity } from '@/lib/geocode'

export function LocationSettings({
  location,
  onSave,
  className,
}: {
  location: { city: string; country: string; capturedAt: number } | null
  onSave: (city: string, country: string, lat: number, lon: number) => void
  className?: string
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  function startEdit() {
    setValue(location?.city ?? '')
    setError(false)
    setEditing(true)
  }

  async function handleSave() {
    const trimmed = value.trim()
    if (!trimmed) return
    setSaving(true)
    setError(false)
    const result = await geocodeCity(trimmed)
    setSaving(false)
    if (!result) {
      setError(true)
      return
    }
    onSave(result.city, result.country, result.lat, result.lon)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className={cn('flex items-center gap-4', className)}>
        <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <MapPinIcon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSave()
                if (e.key === 'Escape') setEditing(false)
              }}
              placeholder={t('location.cityPlaceholder')}
              className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary/50"
            />
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !value.trim()}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded bg-primary text-white dark:text-[#0a0a0a] disabled:opacity-40"
            >
              <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          </div>
          {error && <p className="text-xs text-destructive">{t('settings.location.notFound')}</p>}
        </div>
      </div>
    )
  }

  if (!location) {
    return (
      <div className={cn('flex items-center gap-4', className)}>
        <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <MapPinOffIcon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground/60">
              {t('settings.location.notAvailable')}
            </span>
            <button
              type="button"
              onClick={startEdit}
              className="shrink-0 cursor-pointer text-xs text-primary-text underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              {t('settings.location.set')}
            </button>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground/50">
            {t('settings.location.description')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <MapPinIcon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {location.city}, {location.country}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('settings.location.description')}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={startEdit}
        className="cursor-pointer text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        title={t('settings.location.edit')}
      >
        <PencilIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
    </div>
  )
}
